import {
  ActualityAggregatedHqDataRows,
  ActualityEmployeeShifts,
} from '@warebee/shared/dataset-queries';
import _ from 'lodash';
import {
  AgentLabourEventsSetting,
  agentLabourMetadataPropertyName,
  AgentSettingsWithMeta,
} from '../../resourcePolicy/agentData/agent.types';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregationField,
} from './actuality.kpi.types';

export type ActualityAggregatedHqExtendedDataRowsParams = {
  rows: ActualityAggregatedHqDataRows;
  agents: AgentSettingsWithMeta[];
  shifts: ActualityEmployeeShifts;
};

export function getActualityAggregatedHqExtendedDataRows(
  params: ActualityAggregatedHqExtendedDataRowsParams,
): ActualityAggregatedHqExtendedDataRow[] {
  const agentSettingsById = _.keyBy(params.agents, a => a.id);
  const shiftActualDurationById: Record<string, number> = {};
  const shiftsScheduledDurationById: Record<string, number> = {};
  const shiftDurationLabourById: Record<string, AgentLabourEventsSetting> = {};

  _.forEach(params.shifts, shift => {
    shiftActualDurationById[shift.shiftId] =
      shift.shiftEnd.getTime() - shift.shiftStart.getTime();

    shiftsScheduledDurationById[shift.shiftId] =
      agentSettingsById[shift.employeeId]?.utilisation?.secondsOperation * 1000;

    shiftDurationLabourById[shift.shiftId] =
      agentSettingsById[shift.employeeId]?.metadata?.[
        agentLabourMetadataPropertyName
      ];
  });

  return _(params.rows)
    .map(row => {
      const netTimeMs = row.totalEmployeeActivityDuration ?? 0;
      const netHours = netTimeMs / (3600 * 1000);

      const idleTimeMs =
        (row.totalIdleDurationBetweenJobs ?? 0) +
        (row.totalIdleDurationShiftEnd ?? 0) +
        (row.totalIdleDurationShiftStart ?? 0);
      const grossTimeMs = idleTimeMs + netTimeMs;
      const grossHours = grossTimeMs / (3600 * 1000);
      const uniqueShifts = _(params.rows)
        .flatMap(l => l.shiftId)
        .uniq();

      const breaks = 15;
      const meetings = 15;
      const training = 15;
      const recharge = 15;
      const maintenance = 15;
      const misc = 0;

      // const uniqOrders = _(agentLists)
      //   .flatMap(l => l.jobGroupIds)
      //   .uniq();

      // const shiftLabourEventsDuration: AgentLabourEventsSetting = uniqueShifts
      //   .transform((acc, shiftId) => {
      //     const settings = shiftDurationLabourById[shiftId];

      //     _.forEach(agentLabourEvents, e => {
      //       if (!_.isNil(settings?.[e])) {
      //         acc[e] = settings[e] * 1000 + (acc[e] ?? 0);
      //       }
      //     });
      //   }, {} as AgentLabourEventsSetting)
      //   .value();

      // analyzedCost
      // analyzedDuration
      // analyzedDurationTravelling
      // analyzedDurationTravellingStartEnd
      // analyzedDistance
      // analyzedDistanceStartEnd
      // analyzedDurationHandling
      // analyzedDurationPicking
      // analyzedDurationTravellingVertical
      // analyzedDurationReordering
      // analyzedCost

      //performance NET
      const netLinesPerformance = netHours > 0 ? row.eventsCount / netHours : 0;
      const netUomsPerformance = netHours > 0 ? row.uomCount / netHours : 0;
      const netLocationsPerformance =
        netHours > 0 ? row.visitsCount / netHours : 0;
      const netJobsPerformance = netHours > 0 ? row.jobsCount / netHours : 0;
      const netOrdersPerformance =
        netHours > 0 ? row.jobGroupsCount / netHours : 0;

      //performance GROSS
      const grossLinesPerformance =
        grossHours > 0 ? row.eventsCount / grossHours : 0;
      const grossUomsPerformance =
        grossHours > 0 ? row.uomCount / grossHours : 0;
      const grossLocationsPerformance =
        grossHours > 0 ? row.visitsCount / grossHours : 0;
      const grossJobsPerformance =
        grossHours > 0 ? row.jobsCount / grossHours : 0;
      const grossOrdersPerformance =
        grossHours > 0 ? row.jobGroupsCount / grossHours : 0;

      //performance Shift Actual

      // const shiftsCount = uniqueShifts.size();
      // const ordersCount = uniqOrders.size();
      const shiftActualTime = uniqueShifts
        .map(id => shiftActualDurationById[id])
        .sum();
      const shiftActualHours = shiftActualTime / (3600 * 1000);

      const shiftScheduledTime = uniqueShifts
        .map(id => shiftsScheduledDurationById[id])
        .sum();
      const shiftScheduledHours = shiftScheduledTime / (3600 * 1000);

      const shiftActualLinesPerformance =
        shiftActualHours > 0 ? row.eventsCount / shiftActualHours : 0;
      const shiftActualUomsPerformance =
        shiftActualHours > 0 ? row.uomCount / shiftActualHours : 0;
      const shiftActualLocationsPerformance =
        shiftActualHours > 0 ? row.visitsCount / shiftActualHours : 0;
      const shiftActualJobsPerformance =
        shiftActualHours > 0 ? row.jobsCount / shiftActualHours : 0;
      const shiftActualOrdersPerformance =
        shiftActualHours > 0 ? row.jobGroupsCount / shiftActualHours : 0;

      //performance Shift Scheduled
      const shiftScheduledLinesPerformance =
        shiftScheduledHours > 0 ? row.eventsCount / shiftScheduledHours : 0;
      const shiftScheduledUomsPerformance =
        shiftScheduledHours > 0 ? row.uomCount / shiftScheduledHours : 0;
      const shiftScheduledLocationsPerformance =
        shiftScheduledHours > 0 ? row.visitsCount / shiftScheduledHours : 0;
      const shiftScheduledJobsPerformance =
        shiftScheduledHours > 0 ? row.jobsCount / shiftScheduledHours : 0;
      const shiftScheduledOrdersPerformance =
        shiftScheduledHours > 0 ? row.jobGroupsCount / shiftScheduledHours : 0;

      //performance Shift Direct
      const shiftDirectTime = 0;
      // shiftScheduledTime - _.sum(_.values(shiftLabourEventsDuration));
      const shiftDirectHours = shiftDirectTime / (3600 * 1000);
      const shiftDirectLinesPerformance =
        shiftDirectHours > 0 ? row.eventsCount / shiftDirectHours : 0;
      const shiftDirectUomsPerformance =
        shiftDirectHours > 0 ? row.uomCount / shiftDirectHours : 0;
      const shiftDirectLocationsPerformance =
        shiftDirectHours > 0 ? row.visitsCount / shiftDirectHours : 0;
      const shiftDirectJobsPerformance =
        shiftDirectHours > 0 ? row.jobsCount / shiftDirectHours : 0;
      const shiftDirectOrdersPerformance =
        shiftDirectHours > 0 ? row.jobGroupsCount / shiftDirectHours : 0;

      const shiftsCount = row.shiftsCount;

      //performance per shift
      const perShiftLinesPerformance =
        shiftsCount > 0 ? row.eventsCount / shiftsCount : 0;
      const perShiftUomsPerformance =
        shiftsCount > 0 ? row.uomCount / shiftsCount : 0;
      const perShiftLocationsPerformance =
        shiftsCount > 0 ? row.visitsCount / shiftsCount : 0;
      const perShiftJobsPerformance =
        shiftsCount > 0 ? row.jobsCount / shiftsCount : 0;
      const perShiftOrdersPerformance =
        shiftsCount > 0 ? row.jobGroupsCount / shiftsCount : 0;

      const uomLineRatio =
        row.eventsCount > 1 ? row.uomCount / row.eventsCount : 0;
      const jobLineRatio =
        row.eventsCount > 1 ? row.eventsCount / row.jobsCount : 0;
      const orderLineRatio =
        row.eventsCount > 1 ? row.eventsCount / row.jobGroupsCount : 0;

      const idleNetRatio = netTimeMs > 0 ? idleTimeMs / netTimeMs : 0;
      const netGrossRatio = grossTimeMs > 0 ? netTimeMs / grossTimeMs : 0;
      // const activeShiftRatio =
      //   shiftActualTime > 0 ? netTime / shiftActualTime : 0;
      // const utilisationScheduledRatio =
      //   shiftScheduledTime > 0 ? netTime / shiftScheduledTime : 0;
      const linesPerVisitedLocation =
        row.visitsCount > 0 ? row.eventsCount / row.visitsCount : 0;
      const uomsPerVisitedLocation =
        row.visitsCount > 0 ? row.uomCount / row.visitsCount : 0;
      const locationsPerJob =
        row.jobsCount > 0 ? row.visitsCount / row.jobsCount : 0;
      const locationsPerOrder =
        row.jobGroupsCount > 0 ? row.visitsCount / row.jobGroupsCount : 0;

      // // Calculate total indirect labor time
      // const labourIndirectTotal = _.sum(_.values(shiftLabourEventsDuration));

      // // Calculate unknown time (idle time minus accounted labor activities, but never negative)
      // const unknownTime = Math.max(0, idleTime - labourIndirectTotal);

      // const efficiencyShiftHours =
      //   netHours > 0 ? (shiftScheduledTime / netHours) * 100 : 0;

      const efficiencyHourly =
        netHours > 0 ? (shiftScheduledTime / netHours) * 100 : 0;

      const totalDistancePerLine =
        row.eventsCount > 0 ? row.analyzedDistance / row.eventsCount : 0;
      const totalDistancePerJob =
        row.jobsCount > 0 ? row.analyzedDistance / row.jobsCount : 0;
      const totalDistancePerOrder =
        row.jobGroupsCount > 0 ? row.analyzedDistance / row.jobGroupsCount : 0;

      const distanceInternal = row.analyzedDistanceTravellingInternal;
      const internalDistancePerLine =
        row.eventsCount > 0 ? distanceInternal / row.eventsCount : 0;
      const internalDistancePerJob =
        row.jobsCount > 0 ? distanceInternal / row.jobsCount : 0;
      const internalDistancePerOrder =
        row.jobGroupsCount > 0 ? distanceInternal / row.jobGroupsCount : 0;

      const durationDiff = netTimeMs - row.analyzedDuration * 1000;

      const durationRate =
        netTimeMs > 0 ? (row.analyzedDuration * 1000) / netTimeMs : 0;

      const costPerUnit =
        row.uomCount > 0 ? row.analyzedCost / row.uomCount : 0;

      // const fullTimeEquivalents = shiftScheduledTime / netTime;

      // const FT_WEEK_MILLIS = 40 * 3600 * 1000;

      // /* 1. Paid-hours FTE (scheduled) */
      // const fteScheduled = shiftScheduledTime / FT_WEEK_MILLIS;

      // /* 2. Worked-hours FTE (on-site clock-in to clock-out) */
      // const fteActual = shiftActualTime / FT_WEEK_MILLIS;

      // /* 3. Direct-hours FTE (productive only) */
      // const fteDirect = netTime / FT_WEEK_MILLIS;

      const stats: ActualityAggregatedHqExtendedDataRow = {
        ...row,

        shiftActualTime,
        shiftScheduledTime,
        // shiftDirectTime,
        // labourIndirectTotal,
        // unknownTime,

        idleTimeMs,
        grossTimeMs,
        uomLineRatio,
        jobLineRatio,
        orderLineRatio,

        netLinesPerformance,
        netUomsPerformance,
        netLocationsPerformance,
        netJobsPerformance,
        netOrdersPerformance,

        grossLinesPerformance,
        grossUomsPerformance,
        grossLocationsPerformance,
        grossJobsPerformance,
        grossOrdersPerformance,

        efficiencyHourly,
        // shiftActualLinesPerformance,
        // shiftActualUomsPerformance,
        // shiftActualLocationsPerformance,
        // shiftActualJobsPerformance,
        // shiftActualOrdersPerformance,

        // shiftScheduledLinesPerformance,
        // shiftScheduledUomsPerformance,
        // shiftScheduledLocationsPerformance,
        // shiftScheduledJobsPerformance,
        // shiftScheduledOrdersPerformance,

        perShiftLinesPerformance,
        perShiftUomsPerformance,
        perShiftLocationsPerformance,
        perShiftJobsPerformance,
        perShiftOrdersPerformance,

        // ...shiftLabourEventsDuration,

        // shiftDirectLinesPerformance,
        // shiftDirectUomsPerformance,
        // shiftDirectLocationsPerformance,
        // shiftDirectJobsPerformance,
        // shiftDirectOrdersPerformance,

        // breaks,
        // meetings,
        // training,
        // recharge,
        // maintenance,
        // misc,

        // efficiencyShiftHours,
        // fullTimeEquivalents,

        idleNetRatio,
        netGrossRatio,
        // activeShiftRatio,
        // utilisationScheduledRatio,
        linesPerVisitedLocation,
        uomsPerVisitedLocation,
        locationsPerJob,
        locationsPerOrder,
        totalDistancePerLine,
        totalDistancePerOrder,
        totalDistancePerJob,
        internalDistancePerLine,
        internalDistancePerOrder,
        internalDistancePerJob,
        durationDiff,
        durationRate,
        costPerUnit,
      };

      return stats;
    })
    .value();
}
export function getAggregatedStatsKey(
  stats: ActualityAggregatedHqExtendedDataRow,
  groupFields: ActualityAggregationField[],
): Partial<ActualityAggregatedHqExtendedDataRow> {
  return _.transform(
    groupFields,
    (acc, group) => {
      acc[group] = stats[group];
    },
    {},
  );
}
